/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { renderNodeRule, StructuredText } from 'react-datocms'
import { isHeading } from 'datocms-structured-text-utils'
import { render as toPlainText } from 'datocms-structured-text-to-plain-text'
import slugify from 'slugify'
import classnames from 'classnames'
import CallToAction from 'components/call-to-action'
import { CodeBlock } from '@hashicorp/mds-react/components'
import AudioBlock from './blocks/audio'
import ButtonBlock from './blocks/button'
import ImageBlock from './blocks/image'
import InlineVideoBlock from './blocks/inline-video'
import PeopleBlock from './blocks/people'
import SlidesBlock from './blocks/slides'
import Table from './blocks/table'
import VideoBlock from './blocks/video'

import longFormContentStyle from 'components/longform-content-section/style.module.css'
import s from './style.module.css'

export { default as LegacyContent } from './legacy-content'

export default function SingleResourceContent({ children }) {
	return (
		<section className={s.singleResourceContent}>
			<div className={s.pageLane}>{children}</div>
		</section>
	)
}

export function LongFormContent({ description, content }) {
	return (
		<div
			className={classnames(
				s.longFormContent,
				longFormContentStyle['longformContentSection'],
			)}
			data-cy="resource-long-form-content"
		>
			{description ? <p className="g-type-body-large">{description}</p> : null}
			<StructuredText
				data={content}
				customNodeRules={[
					// Add HTML anchors to heading levels for in-page navigation
					renderNodeRule(isHeading, ({ node, children, key }) => {
						const HeadingTag: React.ElementType = `h${node.level}`
						const anchor = slugify(toPlainText(node), { lower: true })

						return (
							<HeadingTag key={key}>
								<a
									className="__permalink-h"
									href={`#${anchor}`}
									aria-label={`${toPlainText(node).toLowerCase()} permalink`}
								>
									»
								</a>
								<a className="__target-h" id={anchor} aria-hidden="true" />
								{children}
							</HeadingTag>
						)
					}),
				]}
				renderBlock={({ record }: any) => {
					switch (record.__typename) {
						case 'ButtonBlockRecord':
							return (
								<ButtonBlock
									text={record.text}
									url={record.url}
									linkType={record.linkType}
								/>
							)
						case 'ImageBlockRecord':
							return (
								<ImageBlock
									{...{ image: record.image, caption: record.caption }}
								/>
							)
						case 'StCodeBlockRecord':
							return (
								<CodeBlock
									language={record.language.slug}
									value={record.code}
									hasLineNumbers={false}
								/>
							)
						case 'CallToActionBlockRecord':
							return (
								<CallToAction
									heading={record.heading}
									content={record.subheading}
									links={record.links}
									product="primary"
								/>
							)
						case 'PeopleBlockRecord':
							return <PeopleBlock {...(record as any)} />

						case 'SlidesBlockRecord':
							return (
								<SlidesBlock slideshareEmbedKey={record.slideshareEmbedKey} />
							)
						case 'AudioBlockRecord':
							return <AudioBlock record={record} />
						case 'VideoBlockRecord':
							return <VideoBlock url={record.video.url} />
						case 'InlineVideoBlockRecord':
							return (
								<InlineVideoBlock
									url={record.url}
									aspectRatio={record.aspectRatio}
									description={record.description}
									showAccentColor={record.showAccentColor}
								/>
							)
						case 'StTableRecord':
							return <Table {...record} />
						default:
							return null
					}
				}}
			/>
		</div>
	)
}
